import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import clsx from 'clsx';
import { useDebounce } from '@uidotdev/usehooks';
import { v4 as uuidv4 } from 'uuid';

import {
  CALENDAR_EVENT_LOCATION_AUTOCOMPLETE_MIN_QUERY_LENGTH,
  CalendarEventLocationSuggestion,
  useCalendarEventLocationAutocomplete,
} from '../../../../data-access';
import './EventLocationAutocomplete.scss';
import { useTranslation } from 'react-i18next';

type LocationOption = CalendarEventLocationSuggestion & {
  isCustom?: boolean;
};

interface EventLocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

const getLocationOptionLabel = (option: CalendarEventLocationSuggestion | string): string => {
  if (typeof option === 'string') return option;
  return option.formattedText || [option.title, option.subtitle].filter(Boolean).join(', ');
};

const EventLocationAutocomplete: React.FC<EventLocationAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
  className,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const trimmedInput = inputValue.trim();
  const debouncedQuery = useDebounce(trimmedInput, 250);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const ensureSessionToken = useCallback(() => {
    setSessionToken((currentToken) => currentToken ?? uuidv4());
  }, []);

  const resetSessionToken = useCallback(() => {
    setSessionToken(null);
  }, []);

  const { suggestions, isFetching, isError } = useCalendarEventLocationAutocomplete({
    query: debouncedQuery,
    sessionToken,
    enabled: isOpen && !disabled,
  });

  const options = useMemo<LocationOption[]>(() => {
    if (!trimmedInput) return [];

    const normalizedInput = trimmedInput.toLowerCase();
    const customOption: LocationOption = {
      placeId: `custom:${trimmedInput}`,
      title: trimmedInput,
      subtitle: '',
      formattedText: trimmedInput,
      isCustom: true,
    };

    const remoteOptions = suggestions.filter((suggestion) => {
      return getLocationOptionLabel(suggestion).trim().toLowerCase() !== normalizedInput;
    });

    return [customOption, ...remoteOptions];
  }, [suggestions, trimmedInput]);

  const commitLocation = useCallback(
    (nextValue: string) => {
      const normalizedValue = nextValue.trim();
      setInputValue(normalizedValue);
      onChange(normalizedValue);
      setIsOpen(false);
      resetSessionToken();
    },
    [onChange, resetSessionToken]
  );

  return (
    <Autocomplete<LocationOption, false, false, true>
      freeSolo
      autoHighlight
      handleHomeEndKeys
      className={clsx('event-location-autocomplete', className)}
      disabled={disabled}
      filterOptions={(availableOptions) => availableOptions}
      getOptionLabel={getLocationOptionLabel}
      inputValue={inputValue}
      isOptionEqualToValue={(option, selectedOption) => option.placeId === selectedOption.placeId}
      loading={isFetching}
      noOptionsText={
        trimmedInput.length < CALENDAR_EVENT_LOCATION_AUTOCOMPLETE_MIN_QUERY_LENGTH
          ? t('calendar.eventMenu.typeAtLeastCharacters', {
              count: CALENDAR_EVENT_LOCATION_AUTOCOMPLETE_MIN_QUERY_LENGTH,
            })
          : isError
            ? t('calendar.eventMenu.couldNotLoadLocations')
            : t('calendar.eventMenu.noLocationsFound')
      }
      onBlur={resetSessionToken}
      onChange={(_, selectedOption, reason) => {
        if (reason === 'clear') {
          commitLocation('');
          return;
        }

        if (!selectedOption) return;
        commitLocation(getLocationOptionLabel(selectedOption));
      }}
      onClose={() => setIsOpen(false)}
      onFocus={() => {
        ensureSessionToken();
        if (trimmedInput) setIsOpen(true);
      }}
      onInputChange={(_, nextInputValue, reason) => {
        if (reason === 'reset') return;

        if (reason === 'clear') {
          commitLocation('');
          return;
        }

        ensureSessionToken();
        setInputValue(nextInputValue);
        onChange(nextInputValue);
        setIsOpen(Boolean(nextInputValue.trim()));
      }}
      onKeyDown={(event) => {
        event.stopPropagation();
      }}
      onOpen={() => {
        ensureSessionToken();
        if (trimmedInput) setIsOpen(true);
      }}
      open={isOpen && !disabled && Boolean(trimmedInput)}
      openOnFocus
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          className="event-location-autocomplete__input"
          placeholder={t('calendar.eventMenu.addLocation')}
          variant="standard"
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            endAdornment: (
              <>
                {isFetching ? (
                  <CircularProgress
                    className="event-location-autocomplete__loading"
                    color="inherit"
                    size={14}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            'aria-label': t('calendar.eventMenu.eventLocation'),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          className={clsx(props.className, 'event-location-autocomplete__option', {
            'event-location-autocomplete__option--custom': option.isCustom,
          })}
        >
          <span className="event-location-autocomplete__option__marker">
            {option.isCustom ? '+' : option.title.slice(0, 1).toUpperCase()}
          </span>
          <span className="event-location-autocomplete__option__copy">
            <span className="event-location-autocomplete__option__title">
              {option.isCustom
                ? t('calendar.eventMenu.useThisLocation', { location: option.title })
                : option.title}
            </span>
            {option.subtitle && (
              <span className="event-location-autocomplete__option__subtitle">
                {option.subtitle}
              </span>
            )}
          </span>
        </li>
      )}
      slotProps={{
        paper: { className: 'event-location-autocomplete__paper' },
        popper: { className: 'event-location-autocomplete__popper' },
      }}
      value={null}
    />
  );
};

export default EventLocationAutocomplete;
